import React from 'react';
import { Button } from '../../ui/components/Button';
import { Dialog } from '../../ui/components/Dialog';
import { FeatherX, FeatherCheck, FeatherAlertCircle } from '@subframe/core';
import { useSigningRequest } from '../../hooks/useSigningRequest';
import { useWcSigningRequest } from '../../hooks/useWcSigningRequest';
import type { SigningState } from '../../types/signing';
import { getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';

/**
 * WalletSigningModal - Transaction signing modal for XRPL transactions
 *
 * ✅ USE THIS FOR: All transaction signing operations
 * - Selling NFTs (creating sell offers)
 * - Canceling listings
 * - Transferring NFTs
 * - Making buy offers
 * - Any other XRPL transaction that requires a wallet signature
 *
 * HOW IT WORKS:
 * - Takes a wallet-agnostic SigningPayload (use toSigningPayload() to normalize
 *   backend responses) and renders the delivery UI for its provider — QR code +
 *   deep link for Xaman today; client-signing providers get their own rendering
 *   branch when they land (multi-wallet Phase 4).
 * - Resolution (signed/rejected/expired) comes from useSigningRequest.
 *
 * ⚠️ DO NOT USE XamanModal FOR TRANSACTIONS!
 * XamanModal is for authentication only and polls the wrong endpoint (/auth/xaman/verify).
 *
 * FOR BUY FLOWS WITH CONFIRMATION:
 * Use XamanTransactionModal instead - it wraps this component with a confirmation step.
 *
 * @example
 * ```tsx
 * <WalletSigningModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="List NFT for Sale"
 *   payload={toSigningPayload(response.xamanPayload)}
 *   onSuccess={(txid) => handleSuccess(txid)}
 * />
 * ```
 *
 * @see XamanSigningModal - back-compat wrapper over this component (legacy prop shape)
 * @see useSigningRequest - the hook that powers resolution
 */
export interface WalletSigningModalProps {
  // Modal state
  open: boolean;
  onClose: () => void;

  // Modal title
  title: string;

  // Normalized signing payload (any provider)
  payload: SigningPayload;

  // Callbacks
  onSuccess?: (txid: string) => void;
  onRejected?: () => void;
  onExpired?: () => void;

  // Optional custom message (defaults to provider-appropriate signing message)
  message?: string;

  // Optional content rendered between the status message and QR code
  children?: React.ReactNode;

  // Styling
  className?: string;
}

/**
 * Per-provider resolution components: hooks can't branch on provider, so the modal
 * mounts exactly one resolver which owns its resolution hook and renders the body.
 */
const XamanResolver: React.FC<WalletSigningModalProps> = (props) => {
  const state = useSigningRequest({
    payload: props.payload,
    onSigned: (txHash) => props.onSuccess?.(txHash),
    onRejected: () => props.onRejected?.(),
    onExpired: () => props.onExpired?.(),
  });
  return <ModalBody {...props} state={state} />;
};

const WcResolver: React.FC<WalletSigningModalProps> = (props) => {
  const state = useWcSigningRequest({
    payload: props.payload,
    onSigned: (txHash) => props.onSuccess?.(txHash),
    onRejected: () => props.onRejected?.(),
    onExpired: () => props.onExpired?.(),
  });
  return <ModalBody {...props} state={state} />;
};

export const WalletSigningModal: React.FC<WalletSigningModalProps> = (props) => {
  const provider = getWalletProvider(props.payload.provider);
  return provider.capabilities.clientSigning ? <WcResolver {...props} /> : <XamanResolver {...props} />;
};

const ModalBody: React.FC<WalletSigningModalProps & { state: SigningState }> = ({
  open,
  onClose,
  title,
  payload,
  message,
  children,
  state,
}) => {
  const provider = getWalletProvider(payload.provider);
  const defaultMessage = provider.capabilities.clientSigning
    ? `Approve the transaction in your ${provider.name} wallet`
    : `Scan the QR code with your ${provider.name} app and sign to complete this transaction`;
  const pendingMessage = message ?? defaultMessage;

  const { status, txid, error, isWebSocket, expiresInSeconds } = state;

  const handleDeepLinkClick = () => {
    if (payload.deepLink) {
      window.open(payload.deepLink, '_blank');
    }
  };

  const getStatusMessage = (): string => {
    switch (status) {
      case 'pending':
        return pendingMessage;
      case 'opened':
        return `Opened in ${provider.name} app - Please review and sign`;
      case 'signing':
        return 'Signing transaction...';
      case 'dispatching':
        return 'Processing transaction on XRPL...';
      case 'signed':
        return 'Transaction successful!';
      case 'rejected':
        return error || 'Transaction was rejected';
      case 'expired':
        return error || 'Request expired';
      default:
        return pendingMessage;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'signed':
        return <FeatherCheck className="h-6 w-6 text-success-600" />;
      case 'rejected':
      case 'expired':
        return <FeatherAlertCircle className="h-6 w-6 text-error-600" />;
      case 'dispatching':
        return (
          <svg className="h-6 w-6 text-brand-600 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusColor = (): string => {
    switch (status) {
      case 'signed':
        return 'text-success-600';
      case 'rejected':
      case 'expired':
        return 'text-error-600';
      case 'opened':
      case 'signing':
      case 'dispatching':
        return 'text-brand-600';
      default:
        return 'text-subtext-color';
    }
  };

  const awaitingUser = status === 'pending' || status === 'opened';
  const shouldShowQRCode = awaitingUser && provider.capabilities.qr && !!payload.qrUrl;
  const shouldShowDeepLink = awaitingUser && provider.capabilities.deepLink && !!payload.deepLink;
  const isFinalState = status === 'signed' || status === 'rejected' || status === 'expired';

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <Dialog.Content className="w-full max-w-[550px]">
        {/* Header with close button */}
        <div className="flex w-full items-center justify-between px-6 py-4">
          <span className="text-heading-3 font-heading-3 text-default-font">{title}</span>
          <button
            type="button"
            onClick={onClose}
            disabled={status === 'dispatching'}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded hover:bg-neutral-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>
        <div className="w-full h-px bg-neutral-border" />

        {/* Content */}
        <div className="flex flex-row justify-center items-center w-full">
          <div className="flex flex-col items-center gap-6 p-8 max-w-[540px] w-full">
            {/* Status Message */}
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <p className={`text-body font-body text-center ${getStatusColor()}`}>
                {getStatusMessage()}
              </p>
            </div>

            {/* Optional content (e.g., fee breakdown) */}
            {children && awaitingUser && children}

            {/* Transaction Opened Indicator */}
            {isWebSocket && status === 'opened' && (
              <div className="text-caption font-caption text-success-600">
                ✓ Transaction opened
              </div>
            )}

            {/* Expiration Countdown */}
            {expiresInSeconds !== undefined && expiresInSeconds < 120 && status !== 'signed' && status !== 'rejected' && (
              <div className="text-caption font-caption text-warning-600">
                Expires in {Math.floor(expiresInSeconds / 60)}:{String(expiresInSeconds % 60).padStart(2, '0')}
              </div>
            )}

            {/* QR Code - Only show when waiting for scan */}
            {shouldShowQRCode && (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={payload.qrUrl}
                  alt={`${provider.name} QR Code`}
                  style={{ width: 220, height: 220 }}
                />
              </div>
            )}

            {/* Deep Link Button - Only show when waiting for scan */}
            {shouldShowDeepLink && (
              <div className="flex flex-col items-center gap-2 w-full">
                <Button
                  variant="neutral-secondary"
                  size="large"
                  onClick={handleDeepLinkClick}
                  className="w-full"
                >
                  Open in {provider.name}
                </Button>
              </div>
            )}

            {/* Transaction Hash - Show when signed */}
            {status === 'signed' && txid && (
              <div className="flex flex-col items-center gap-2 w-full p-4 bg-success-50 rounded-md border border-success-200">
                <span className="text-caption-bold font-caption-bold text-success-800">Transaction Hash</span>
                <span className="text-caption font-caption text-success-700 font-mono break-all text-center">
                  {txid}
                </span>
              </div>
            )}

            {/* Error Details - Show when rejected or expired */}
            {(status === 'rejected' || status === 'expired') && error && (
              <div className="flex flex-col items-center gap-2 w-full p-4 bg-error-50 rounded-md border border-error-200">
                <span className="text-body font-body text-error-800 text-center">
                  {error}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="w-full h-px bg-neutral-border" />
        <div className="flex w-full items-center justify-end gap-3 px-6 py-5">
          {isFinalState ? (
            <Button
              variant="brand-primary"
              size="large"
              onClick={onClose}
            >
              {status === 'signed' ? 'Done' : 'Close'}
            </Button>
          ) : (
            <Button
              variant="neutral-secondary"
              size="large"
              onClick={onClose}
              disabled={status === 'dispatching'}
            >
              Cancel
            </Button>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
